import { ColumnDef } from '@tanstack/react-table';
import { FaChevronDown } from 'react-icons/fa';
import { FaArrowUpRightFromSquare } from 'react-icons/fa6';

import TableCellDate from '@/components/atoms/TableCellDate';
import TableCellText from '@/components/atoms/TableCellText';
import {
	EmployeeByActivityPoleCondition,
	EmployeeByActivityPoleConditionTraining,
} from '@/types/EmployeeByActivityPole';
import { cn } from '@/utils/cn';
import { removeTimeZone } from '@/utils/formatDate';

import {
	EmployeeByActivityPoleTrainingStatus,
	employeeByActivityPoleTrainingStatusesColors,
} from '../EmployeeByActivityPoleConstants';

export const employeeByActivityPoleTrainingColumns: (
	employeeId: number,
	allowed: boolean,
) => Array<ColumnDef<EmployeeByActivityPoleCondition>> = (
	employeeId,
	allowed,
) => {
	return [
		{
			accessorKey: 'id',
			header: '',
			cell: (props) => {
				const trainings = props.row.original
					.employeeByActivityPoleConditionTraining as EmployeeByActivityPoleConditionTraining[];
				const uniqueTrainingOnCondition = trainings?.length === 1;
				if (uniqueTrainingOnCondition) {
					return <div className="h-12 w-10"></div>;
				}
				return (
					<>
						<div className="mx-auto flex h-12 w-10 items-center">
							<button
								onClick={() => props.row.toggleExpanded()}
								className="flex h-12 w-full items-center justify-center px-2"
							>
								<FaChevronDown
									className={cn('mr-2 h-3  text-primary transition-transform', {
										'-mt-4 rotate-180': props.row.getIsExpanded(),
									})}
								/>
							</button>
						</div>
						{props.row.getIsExpanded() && (
							<>
								{trainings.map((_, index) => (
									<div className="h-12" key={index}></div>
								))}
							</>
						)}
					</>
				);
			},
		},
		{
			accessorKey: 'employeeByActivityPoleConditionTraining',
			header: 'TREINAMENTO',
			meta: {
				headerClassName: 'w-[50rem] mr-2',
			},
			cell: (props) => {
				const trainingNames = (
					props.getValue() as EmployeeByActivityPoleConditionTraining[]
				).map((it) => it.training.name);

				return (
					<>
						<div className="mr-2 flex h-12 w-[50rem] items-center uppercase">
							<TableCellText text={trainingNames.join(', ')} width="100%" />
						</div>
						{props.row.getIsExpanded() && (
							<>
								{trainingNames.map((name, index) => (
									<div
										key={index}
										className="W-96 my-1 flex h-12 items-center rounded-l-md bg-gray-200"
									>
										<TableCellText text={name} width="50rem" className="pl-2" />
									</div>
								))}
							</>
						)}
					</>
				);
			},
			enableSorting: false,
		},
		{
			accessorKey: 'status',
			header: 'STATUS',
			cell: (props) => {
				const status = props.getValue() as string;
				const employeeTrainings =
					props.row.original.employeeByActivityPoleConditionTraining;
				return (
					<>
						<div className="flex h-12  w-40 items-center">
							<div
								className={cn(
									'flex min-w-[3rem] text-nowrap rounded p-[5px] text-[11px] uppercase tracking-widest text-white',
									employeeByActivityPoleTrainingStatusesColors[
										props.getValue() as EmployeeByActivityPoleTrainingStatus
									],
								)}
							>
								{status}
							</div>
						</div>
						{props.row.getIsExpanded() && (
							<>
								{employeeTrainings.map(({ status, createdByMatrix }, index) => {
									const backgroundColor =
										status === EmployeeByActivityPoleTrainingStatus.PENDING &&
										!createdByMatrix
											? 'bg-gray-400'
											: employeeByActivityPoleTrainingStatusesColors[
													status as EmployeeByActivityPoleTrainingStatus
												];
									return (
										<div
											key={index}
											className="my-1 flex h-12  items-center bg-gray-200"
										>
											<div className="flex h-12  items-center">
												<div
													className={cn(
														'flex min-w-[3rem] text-nowrap rounded p-[5px] text-[11px] uppercase tracking-widest text-white',
														backgroundColor,
													)}
												>
													{status}
												</div>
											</div>
										</div>
									);
								})}
							</>
						)}
					</>
				);
			},
			enableSorting: false,
		},
		{
			header: allowed ? 'PREVISÃO DE BLOQUEIO' : 'DATA DE BLOQUEIO',
			accessorKey: 'blockingDate',
			cell: (props) => {
				const value = props.getValue() as string;
				const employeeTrainings =
					props.row.original.employeeByActivityPoleConditionTraining;
				return (
					<>
						<div className="flex h-12 w-32 items-center justify-between">
							<TableCellDate
								date={
									value && value !== '-'
										? removeTimeZone(new Date(value as string))
										: undefined
								}
								emptyValue="-"
								className="flex h-12 w-32 items-center justify-start"
							/>
						</div>
						{props.row.getIsExpanded() && (
							<>
								{employeeTrainings.map((_, index) => (
									<div
										key={index}
										className="my-1 flex h-12 items-center bg-gray-200"
									>
										<TableCellDate
											date={
												value && value !== '-'
													? removeTimeZone(new Date(value as string))
													: undefined
											}
											emptyValue="-"
											className="flex h-12 w-32 items-center justify-start"
										/>
									</div>
								))}
							</>
						)}
					</>
				);
			},
			enableSorting: false,
		},
		{
			accessorKey: 'employeeByActivityPoleConditionTraining',
			header: 'VALIDADE DO TREINAMENTO',
			cell: (props) => {
				const trainings =
					props.getValue() as EmployeeByActivityPoleConditionTraining[];
				const uniqueTrainingOnCondition = trainings?.length === 1;
				const value = uniqueTrainingOnCondition
					? trainings[0].expirationDate
					: '-';
				const expirationDates = trainings.map((it) => it.expirationDate);
				return (
					<>
						<div className="flex h-12 w-32 items-center justify-between">
							<TableCellDate
								date={
									value && value !== '-'
										? removeTimeZone(new Date(value))
										: undefined
								}
								emptyValue="-"
								className="flex h-12 w-32 items-center justify-start"
							/>
						</div>
						{props.row.getIsExpanded() && (
							<>
								{expirationDates.map((expirationDate, index) => (
									<div
										key={index}
										className="my-1 flex h-12 items-center bg-gray-200"
									>
										<TableCellDate
											date={
												expirationDate
													? removeTimeZone(new Date(expirationDate))
													: undefined
											}
											className="flex h-12 w-32 items-center justify-start"
											emptyValue="-"
										/>
									</div>
								))}
							</>
						)}
					</>
				);
			},
			enableSorting: false,
		},
		{
			accessorKey: 'employeeByActivityPoleConditionTraining',
			header: 'PRÓXIMO TREINAMENTO',
			cell: (props) => {
				const trainings =
					props.getValue() as EmployeeByActivityPoleConditionTraining[];
				const uniqueTrainingOnCondition = trainings?.length === 1;
				const nextTrainingDate = uniqueTrainingOnCondition
					? trainings[0].nextTrainingDate
					: undefined;
				return (
					<>
						<div className="flex h-12 w-32 items-center justify-start">
							<TableCellDate
								date={
									nextTrainingDate
										? removeTimeZone(new Date(nextTrainingDate))
										: undefined
								}
								emptyValue="-"
								className="flex h-12 w-32 items-center justify-start"
							/>
						</div>
						{props.row.getIsExpanded() && (
							<>
								{trainings.map((training, index) => (
									<div
										key={index}
										className="my-1 flex h-12 items-center bg-gray-200"
									>
										<TableCellDate
											date={
												training.nextTrainingDate
													? removeTimeZone(new Date(training.nextTrainingDate))
													: undefined
											}
											emptyValue="-"
											className="flex h-12 w-32 items-center justify-start"
										/>
									</div>
								))}
							</>
						)}
					</>
				);
			},
			enableSorting: false,
		},
		{
			accessorKey: 'employeeByActivityPoleConditionTraining',
			header: 'DETALHES',
			cell: (props) => {
				const trainings =
					props.getValue() as EmployeeByActivityPoleConditionTraining[];
				const uniqueTrainingOnCondition = trainings?.length === 1;
				const handleClick = (trainingId: number) => {
					window.open(
						`/home/controle-treinamentos?treinamento=${trainingId.toString()}&colaborador=${employeeId.toString()}`,
						'_blank',
					);
				};
				if (uniqueTrainingOnCondition) {
					return (
						<div className="flex h-12 w-16 items-center justify-center">
							<button
								className="flex h-6 w-6 items-center justify-center rounded-full bg-primary hover:bg-primary/70"
								onClick={() => handleClick(trainings[0].training.id)}
							>
								<FaArrowUpRightFromSquare color="white" size={12} />
							</button>
						</div>
					);
				}
				return (
					<div>
						<div className="h-12"></div>
						{props.row.getIsExpanded() && (
							<>
								{trainings.map((training, index) => (
									<div
										className="my-1 flex h-12 w-16  items-center justify-center rounded-r-md bg-gray-200"
										key={index}
									>
										<button
											className="flex h-6 w-6 items-center justify-center rounded-full bg-primary hover:bg-primary/70"
											onClick={() => handleClick(training.training.id)}
										>
											<FaArrowUpRightFromSquare color="white" size={12} />
										</button>
									</div>
								))}
							</>
						)}
					</div>
				);
			},
			enableSorting: false,
		},
	];
};
